// Keep first: eagerly initializes the RTE plugin subpaths ./utils imports, so a cross-layer module cycle can't leave plugin exports undefined mid-init (RP-5979).
import './utils';
import { useReducer, useEffect, useCallback, useMemo, useState, useRef } from 'react';
import usePrevious from 'react-use/esm/usePrevious';
import useLatest from 'react-use/esm/useLatest';
import prop from 'ramda/es/prop';
import uniqBy from 'ramda/es/uniqBy';
import type {
  UpdateReportMutation,
  UpdateReportMutationVariables,
  SubmitReportMutation,
  SubmitReportMutationVariables,
  CancelReportMutation,
  CancelReportMutationVariables,
  CreateAddendumMutation,
  CreateAddendumMutationVariables,
  UpdateAddendumMutation,
  UpdateAddendumMutationVariables,
  DeleteAddendumMutation,
  DeleteAddendumMutationVariables,
  SubmitAddendumMutation,
  SubmitAddendumMutationVariables,
  CancelAddendumMutation,
  CancelAddendumMutationVariables,
  SubmitPrelimReportMutation,
  SubmitPrelimReportMutationVariables,
  CancelPrelimReportMutation,
  CancelPrelimReportMutationVariables,
} from 'generated/graphql';
import { ReporterSaveState } from 'config/recoilState';
import { useSetRecoilState, useRecoilState, useRecoilValue } from 'recoil';
import {
  UPDATE_REPORT,
  CREATE_ADDENDUM,
  SUBMIT_ADDENDUM,
  UPDATE_ADDENDUM,
  DELETE_ADDENDUM,
  SUBMIT_REPORT,
  CANCEL_REPORT,
  CANCEL_ADDENDUM,
  SUBMIT_PRELIM_REPORT,
  CANCEL_PRELIM_REPORT,
} from 'modules/Apollo/queries';
import { useMutation } from '@apollo/client';
import type { SlateContent } from '../types';
import type { OnSubmitParams } from './types';
import {
  reportStatusState,
  ReportStatus,
  hasReportContentChangedState,
  emptyPlaceholderDialogState,
  draftReportWorklistSmid,
  unsignedReportDialogState,
  reporterIsBypassAndSign,
  reporterIsBypassAndSignPrelim,
  prelimReportWorklistSmid,
  discardReportState,
  headingErrorState,
  rightDrawerCatalogRefreshSequenceState,
} from '../state';
import type { ReportStatuses } from '../state';
import { usePicklistStore } from '../../Template/usePicklist';
import { useCountdown } from './useCountdown';
import {
  worklistUpdateBc,
  WorklistRefetchReason,
} from 'modules/EventsManager/worklistUpdateChannel';
import type { RichTextEditorComponentProps } from '../../RichTextEditor';
import { useRecorder } from 'common/Recorder/useRecorder';
import { useToasterDispatch } from 'common/ui/Toaster';
import { type ToastKey } from 'common/ui/Toaster/Toast';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useSyncHeadingErrorState } from '../../RichTextEditor/plugins/headingError/useSyncHeadingErrorState';
import { useHeadingKeywords } from 'hooks/useHeadingKeywords';
import { buildHeadingLabelSet } from '../../RichTextEditor/plugins/headingDecoration/matcher';
import { useEditorBackup } from 'hooks/useEditorBackup';
import { useTypedCharactersActions } from 'hooks/useTypedCharacters';
import { RICH_TEXT_EDITOR } from 'hooks/useMostRecentInput';
import { useRequiredFieldIndicator } from 'hooks/useRequiredFieldIndicator';
import { FF, useSplitFlag, useFeatureFlagEnabled } from 'modules/feature-flags';
import { useCurrentCaseReport } from 'hooks/useCurrentCaseReport';
import { useBroadcastRefreshCurrentCase } from 'common/CurrentCase/useBroadcastRefreshCurrentCase';
import { useUnsignedReportWarningDialog } from '../UnsignedReportWarningDialog/useUnsignedReportWarningDialog';
import { useWorklistItemAnalytics } from 'hooks/useWorklistItemAnalytics';
import { useDiscardReport } from 'hooks/useDiscardReport';
import { useFocusMode, FocusModeToggleMethod } from 'hooks/useFocusMode';
import {
  HandleAutoloadNextCaseParams,
  useWorklistAutoLoad,
  useMaybeAutoloadNextCase,
} from 'hooks/useWorklistAutoLoad';

import type { LoadedReportWorklistItem, ReportTemplate } from 'hooks/useCurrentCaseReport';
import type { Macros } from '../../Template/usePicklist';
import { useReportVersion } from 'hooks/useReportVersion';
import type { AutoloadTemplateState } from 'hooks/useAutoloadTemplate';
import { useReporterHeartbeat } from '../hooks/useReporterHeartbeat';

import { useReportExitActionData } from 'domains/reporter/RichTextEditor/analytics/useReportExitActionData';
import type { TemplateInsertionContext } from 'domains/reporter/RichTextEditor/analytics/performance';
import { telemetryContextFor } from 'modules/Apollo/types';
import { useSlateContext } from '../SlateContext';
import { isWorkspaceManagerAvailable } from 'modules/workspace-manager';
import { WorkstationIDSubmissionDialog } from './Dialog/WorkstationIDSubmissionDialog';
import { useSubmissionWorkstationID } from './useSubmissionWorkstationID';
import { useIsLinkCasesMutating } from '../LinkCases/hooks/useIsLinkCasesMutating';

import { useEmptyRequiredFieldsDialog } from './useEmptyRequiredFieldsDialog';
import {
  fieldsReducer,
  initFieldsState,
  ADDENDUM_NAME_PLACEHOLDER,
  type FieldsState,
} from './fieldsReducer';
import { isAddendumStatus } from './reportStatusGuards';
import { useAutoSaveReport, type UpdateType } from './useAutoSaveReport';
import { runAutoloadNextCaseWithBroadcast } from './reportExitActionEffects';
import { useAddendumWorkflow } from './useAddendumWorkflow';
import { useSubmitReport } from './useSubmitReport';
import { useDraftReport } from './useDraftReport';
import { useTemplateWorkflow } from './useTemplateWorkflow';
import { useReporterInputBindings } from './useReporterInputBindings';
import { useReportStatusLifecycle } from './useReportStatusLifecycle';
import {
  FieldsStateContext,
  FieldsCountdownContext,
  FieldsDispatchContext,
  FieldsReadinessContext,
} from './fieldsContext';

export const newline = { type: 'paragraph', children: [{ text: '' }] } as const;

export { ADDENDUM_NAME_PLACEHOLDER };
export type { FieldsState };

// Public context surface now lives in ./fieldsContext; re-exported here to keep the import path stable.
export {
  useFieldsState,
  useFieldsCountdown,
  useFieldsDispatch,
  useFieldsReadiness,
  useFields,
} from './fieldsContext';
export type {
  FieldsCountdownContextProps,
  FieldsReadinessContextProps,
  FieldsDispatch,
} from './fieldsContext';

const defaultToastOptions = {
  severity: 'error',
  position: 'bottom-right',
} as const;

export type FieldsProviderProps = Readonly<{
  worklistItem: LoadedReportWorklistItem;
  template?: ReportTemplate;
  clearTemplate: () => void;
  isDisabled: boolean;
  children?: React.ReactNode;
  reporterToolbarMountPoint: Nullable<HTMLElement>;
  isClaimedByMe: boolean;
  onInit?: RichTextEditorComponentProps['onInit'];
  onDestroy?: RichTextEditorComponentProps['onDestroy'];
  onError?: RichTextEditorComponentProps['onError'];
  autoloadTemplateState?: AutoloadTemplateState;
  insertTemplate: (
    template: ReportTemplate,
    insertionContext?: TemplateInsertionContext
  ) => boolean;
}>;

export const FieldsProvider = ({
  children,
  worklistItem,
  template,
  isDisabled,
  reporterToolbarMountPoint,
  insertTemplate,
  isClaimedByMe,
  autoloadTemplateState,
}: FieldsProviderProps): React.ReactElement => {
  const previousWorklistItemRef = useRef<LoadedReportWorklistItem | null>(null);
  const [state, dispatch] = useReducer(
    fieldsReducer,
    {
      worklistItem,
      previousWorklistItemRef,
      reporterToolbarMountPoint,
    },
    initFieldsState
  );
  const { content, activateTemplateID, macros, addendumSMID, addendum } = state;
  const reducerContentRef = useRef(content);
  const latestReportContentRef = useRef(content);
  const latestReportUpdateTypeRef = useRef<Nullable<UpdateType>>(null);
  const pendingReportChangeTypeRef = useRef<Nullable<UpdateType>>(null);

  if (reducerContentRef.current !== content) {
    reducerContentRef.current = content;
    latestReportContentRef.current = content;
    latestReportUpdateTypeRef.current = null;
    pendingReportChangeTypeRef.current = null;
  }

  const getCurrentReportContent = useCallback(() => latestReportContentRef.current, []);
  const getCurrentReportUpdateType = useCallback(() => latestReportUpdateTypeRef.current, []);
  const markNextReportChangeAsTemplateApply = useCallback(() => {
    pendingReportChangeTypeRef.current = 'templateApply';
  }, []);
  const clearPendingReportChangeType = useCallback(() => {
    pendingReportChangeTypeRef.current = null;
  }, []);

  const analyticsData = useWorklistItemAnalytics({ context: APOLLO_QUERY_CONTEXT });
  const previousAddendum = usePrevious(addendum);
  const [isReporterSubmissionEnabled] = useSplitFlag(
    FF.ENABLE_REPORTER_SUBMISSION,
    (v) => v === 'on'
  );
  const { picklists, setPicklists, setMacros, macros: insertedMacros } = usePicklistStore();
  const [hasReportContentChanged, setHasReportContentChanged] = useRecoilState(
    hasReportContentChangedState
  );
  const setDraftReportWorklistSmid = useSetRecoilState(draftReportWorklistSmid);
  const setPrelimReportWorklistSmid = useSetRecoilState(prelimReportWorklistSmid);
  const setRightDrawerCatalogRefreshSequence = useSetRecoilState(
    rightDrawerCatalogRefreshSequenceState
  );
  const [unsignedReportDialogOptions, setUnsignedReportWarningDialogState] =
    useRecoilState(unsignedReportDialogState);
  const [status, setStatus] = useRecoilState<ReportStatuses>(reportStatusState);
  const hasHeadingError = useRecoilValue<boolean>(headingErrorState);
  const isLinkCasesMutating = useIsLinkCasesMutating();
  const { data } = useCurrentUser({ context: APOLLO_QUERY_CONTEXT });
  const { refetch: refetchCurrentCase, currentCaseId, isAddendumLocked } = useCurrentCaseReport();
  const broadcastRefreshCase = useBroadcastRefreshCurrentCase();
  const { onDialogActionSuccess, onDialogActionError } = useUnsignedReportWarningDialog();
  const { handleDiscardReport, handleOpenDiscardReportModal, handleKeepDraft } = useDiscardReport({
    context: APOLLO_QUERY_CONTEXT,
  });
  const discardReportOptions = useRecoilValue(discardReportState);
  const { toggleFocusMode, isFocusModeOn, isFocusModeSettingEnabled } = useFocusMode({
    context: APOLLO_QUERY_CONTEXT,
  });
  const isBypassAndSign = useRecoilValue(reporterIsBypassAndSign);
  const isBypassAndSignPrelim = useRecoilValue(reporterIsBypassAndSignPrelim);
  const { incrementReportVersion } = useReportVersion();
  useReporterHeartbeat({
    context: APOLLO_QUERY_CONTEXT,
    reportStatus: status,
  });
  const [isPrelimSubmissionEnabled] = useFeatureFlagEnabled(FF.REPORTER_PRELIM);
  const [isAutoImpressionsEnabled] = useFeatureFlagEnabled(FF.REPORTER_AUTO_IMPRESSIONS);
  const [isReportFacadeEnabled] = useFeatureFlagEnabled(FF.REPORT_FACADE);
  const [isWorklistUnifiedFieldsEnabled] = useFeatureFlagEnabled(FF.WORKLIST_UNIFIED_FIELDS);
  // RP-6009 (M6): when headings are decorated paragraphs, the duplicate-header sign gate
  // dedupes heading-like paragraph labels via the matcher label set. Skip the query when the
  // flag is off so flag-off stays byte-identical.
  const [isHeadingNodesDeprecated] = useFeatureFlagEnabled(FF.REPORTER_HEADING_NODES_DEPRECATED);
  const headingKeywords = useHeadingKeywords({
    context: APOLLO_QUERY_CONTEXT,
    skip: !isHeadingNodesDeprecated,
  });
  const headingLabelSet = useMemo(() => buildHeadingLabelSet(headingKeywords), [headingKeywords]);
  const { workstationID, resolveSubmissionWorkstationID, workstationPromptDialogProps } =
    useSubmissionWorkstationID({
      addendumSMID,
      worklistItem,
    });

  const isExtensionAvailable = isWorkspaceManagerAvailable();
  const isFocusModeAllowed = useMemo(() => {
    return (
      isFocusModeSettingEnabled &&
      !isDisabled &&
      ([ReportStatus.Idle, ReportStatus.Init] as unknown as Partial<ReportStatuses>).includes(
        status
      )
    );
  }, [isFocusModeSettingEnabled, isDisabled, status]);

  const me = data?.me;
  const [updateReport] = useMutation<UpdateReportMutation, UpdateReportMutationVariables>(
    UPDATE_REPORT,
    {
      context: APOLLO_QUERY_CONTEXT,
    }
  );
  const [submitReport] = useMutation<SubmitReportMutation, SubmitReportMutationVariables>(
    SUBMIT_REPORT,
    {
      context: APOLLO_QUERY_CONTEXT,
    }
  );
  const [cancelReport] = useMutation<CancelReportMutation, CancelReportMutationVariables>(
    CANCEL_REPORT,
    {
      context: APOLLO_QUERY_CONTEXT,
    }
  );
  const [submitPrelimReport] = useMutation<
    SubmitPrelimReportMutation,
    SubmitPrelimReportMutationVariables
  >(SUBMIT_PRELIM_REPORT, {
    context: APOLLO_QUERY_CONTEXT,
  });
  const [cancelPrelimReport] = useMutation<
    CancelPrelimReportMutation,
    CancelPrelimReportMutationVariables
  >(CANCEL_PRELIM_REPORT, {
    context: APOLLO_QUERY_CONTEXT,
  });
  const [createAddendum] = useMutation<CreateAddendumMutation, CreateAddendumMutationVariables>(
    CREATE_ADDENDUM,
    {
      context: APOLLO_QUERY_CONTEXT,
    }
  );
  const [submitAddendumMutation] = useMutation<
    SubmitAddendumMutation,
    SubmitAddendumMutationVariables
  >(SUBMIT_ADDENDUM, {
    context: APOLLO_QUERY_CONTEXT,
  });
  const [updateAddendum] = useMutation<UpdateAddendumMutation, UpdateAddendumMutationVariables>(
    UPDATE_ADDENDUM,
    {
      context: APOLLO_QUERY_CONTEXT,
    }
  );
  const [deleteAddendum] = useMutation<DeleteAddendumMutation, DeleteAddendumMutationVariables>(
    DELETE_ADDENDUM,
    {
      context: APOLLO_QUERY_CONTEXT,
    }
  );
  const [cancelAddendum] = useMutation<CancelAddendumMutation, CancelAddendumMutationVariables>(
    CANCEL_ADDENDUM,
    {
      context: APOLLO_QUERY_CONTEXT,
    }
  );
  const { enqueueToast, enqueueOrUpdateToast, closeToast } = useToasterDispatch();
  const [latestToastKey, setLatestToastKey] = useState<Nullable<ToastKey>>(null);
  const { countdownSeconds, startCountdown, cancelCountdown, clearTimer } = useCountdown({
    worklistItemSmid: worklistItem.smid,
  });
  const { trackTypedCharacters } = useTypedCharactersActions({
    id: RICH_TEXT_EDITOR,
    context: APOLLO_QUERY_CONTEXT,
  });
  const { isRecording, stopRecording, restartRecording } = useRecorder();
  const { resetEditorWithContent } = useSlateContext();

  const { setEditorBackup, restoreEditorBackup } = useEditorBackup();
  const { setAllRequiredFieldsTouched } = useRequiredFieldIndicator();

  const {
    isWorklistAutoLoadEnabled,
    loadDiscardedSmidsFromSessionStorage,
    saveDiscardedSmidsInSessionStorage,
  } = useWorklistAutoLoad({
    context: APOLLO_QUERY_CONTEXT,
  });

  const maybeAutoloadNextCase = useMaybeAutoloadNextCase({
    previousToastKey: latestToastKey,
    onToastDisplayed: (key?: Nullable<ToastKey>) => {
      setLatestToastKey(key);
    },
  });

  const maybeAutoloadNextCaseCallback = useCallback(
    (
      isEnabled: boolean,
      defaultToastMsg: string,
      autoloadParams: HandleAutoloadNextCaseParams
    ): Promise<void> =>
      runAutoloadNextCaseWithBroadcast({
        maybeAutoloadNextCase,
        isWorklistAutoLoadEnabled: isEnabled,
        defaultToastMsg,
        autoloadParams,
      }),
    [maybeAutoloadNextCase]
  );

  useEffect(() => {
    setMacros((macroState: Macros): Macros => {
      const oldMacros = macroState ?? [];
      const newMacros = macros ?? [];
      return uniqBy(prop('smid'), [...oldMacros, ...newMacros]);
    });
  }, [setMacros, macros]);

  useReportStatusLifecycle({
    worklistItem,
    previousWorklistItemRef,
    countdownSeconds,
    startCountdown,
    setStatus,
    dispatch,
    isBypassAndSign,
    isBypassAndSignPrelim,
    isClaimedByMe,
    isAddendumLocked,
    isWorklistUnifiedFieldsEnabled,
    setHasReportContentChanged,
    setDraftReportWorklistSmid,
    setUnsignedReportWarningDialogState,
    status,
    addendumSMID,
    me,
    clearTimer,
    cancelCountdown,
    setPrelimReportWorklistSmid,
    setRightDrawerCatalogRefreshSequence,
    analyticsData,
  });

  useEffect(() => {
    if (reporterToolbarMountPoint != null) {
      dispatch({
        type: 'setReporterToolbarMountPoint',
        payload: {
          reporterToolbarMountPoint,
        },
      });
    }
  }, [reporterToolbarMountPoint]);

  const {
    setSavingState,
    fieldsUpdate,
    fieldsUpdateDebounced,
    addendumUpdate,
    addendumUpdateDebounced,
  } = useAutoSaveReport({
    worklistItem,
    isReportFacadeEnabled,
    updateReport,
    updateAddendum,
    isClaimedByMe,
    workstationID,
    picklists,
    insertedMacros,
    enqueueToast,
    closeToast,
    maybeAutoloadNextCaseCallback,
    isWorklistAutoLoadEnabled,
    setDraftReportWorklistSmid,
    setHasReportContentChanged,
    setEditorBackup,
    restoreEditorBackup,
    isRecording,
    stopRecording,
    refetchCurrentCase,
    incrementReportVersion,
  });

  const contentForHeadingErrorState = isAddendumStatus(status)
    ? (addendum?.content ?? [])
    : getCurrentReportContent();
  const syncHeadingErrorState = useSyncHeadingErrorState(
    contentForHeadingErrorState,
    isHeadingNodesDeprecated ? headingLabelSet : undefined
  );

  const onReportChange = useCallback(
    (newContent: SlateContent) => {
      if (newContent === latestReportContentRef.current) {
        pendingReportChangeTypeRef.current = null;
        return;
      }

      latestReportContentRef.current = newContent;
      const updateType = pendingReportChangeTypeRef.current ?? 'contentChange';
      pendingReportChangeTypeRef.current = null;
      latestReportUpdateTypeRef.current = updateType;
      syncHeadingErrorState(newContent);

      if (isDisabled || status !== ReportStatus.Idle) {
        return;
      }

      setSavingState(ReporterSaveState.Saving);
      fieldsUpdateDebounced({
        contentToSubmit: newContent,
        activateTemplateIDToSubmit: activateTemplateID,
        updateType,
      });

      if (updateType === 'templateApply') {
        fieldsUpdateDebounced.flush();
      }
    },
    [
      activateTemplateID,
      fieldsUpdateDebounced,
      isDisabled,
      setSavingState,
      status,
      syncHeadingErrorState,
    ]
  );

  useEffect(() => {
    // When the current user no longer holds the claim, an open draft addendum must fall back to
    // Submitted so submit actions they can no longer perform aren't offered (e.g. the case was
    // unclaimed or the addendum discarded from another surface/tab). The one exception is a reader
    // viewing another user's still-live draft: that stays visible read-only. We detect it by the
    // draft still being present in the report and owned by a different user. Any other case — the
    // user's own draft, or a draft already removed server-side by a discard — clears the local copy
    // regardless of render ordering between the unclaim and the server-side delete.
    const isAnotherUsersLiveDraft = worklistItem?.report?.addendums?.some(
      ({ sentAt, owner }) => sentAt == null && owner?.id != null && owner.id !== me?.id
    );
    if (
      !isClaimedByMe &&
      status === ReportStatus.AddendumAdd &&
      addendum != null &&
      !isAnotherUsersLiveDraft
    ) {
      dispatch({ type: 'clearAddAddendum' });
      setStatus(ReportStatus.Submitted);
      addendumUpdateDebounced.cancel();
      setSavingState(ReporterSaveState.Saved);
    }
  }, [
    isClaimedByMe,
    worklistItem,
    me,
    status,
    addendum,
    setStatus,
    addendumUpdateDebounced,
    setSavingState,
  ]);

  const onAddendumChange = useCallback(
    (newAddendumContent: SlateContent) => {
      dispatch({
        type: 'updateAddendumContent',
        payload: { addendumContent: newAddendumContent },
      });
    },
    [dispatch]
  );

  const { handleSaveDraft } = useDraftReport({
    setSavingState,
    fieldsUpdate,
    addendumUpdate,
    autoloadTemplateState,
    worklistItem,
    status,
    getCurrentReportContent,
    getCurrentReportUpdateType,
    activateTemplateID,
    addendum,
    addendumSMID,
    isRecording,
    stopRecording,
    loadDiscardedSmidsFromSessionStorage,
    saveDiscardedSmidsInSessionStorage,
  });

  const { handleInsertTemplate } = useTemplateWorkflow({
    template,
    worklistItem,
    status,
    autoloadTemplateState,
    isDisabled,
    dispatch,
    insertTemplate,
    setPicklists,
    setHasReportContentChanged,
    markNextReportChangeAsTemplateApply,
    clearPendingReportChangeType,
  });

  const [placeholderFieldDialogState, setPlaceholderFieldDialogState] = useRecoilState(
    emptyPlaceholderDialogState
  );
  const {
    isOpenForCurrentCase: isEmptyRequiredFieldsDialogOpenForCurrentCase,
    open: openEmptyRequiredFieldsDialog,
  } = useEmptyRequiredFieldsDialog(worklistItem.smid);

  const { measureReportExitActionStartWithMetadata, measureReportExitActionEndWithMetadata } =
    useReportExitActionData();

  const { handleAddendumAdd, handleCancelAddAddendum, cancelAddendumSubmit, submitAddendum } =
    useAddendumWorkflow({
      worklistItem,
      addendumSMID,
      createAddendum,
      deleteAddendum,
      cancelAddendum,
      dispatch,
      setStatus,
      cancelCountdown,
      setRightDrawerCatalogRefreshSequence,
      analyticsData,
      resetEditorWithContent,
      isRecording,
      restartRecording,
      refetchCurrentCase,
      broadcastRefreshCase,
      enqueueToast,
      addendumUpdateDebounced,
      maybeAutoloadNextCaseCallback,
      isWorklistAutoLoadEnabled,
      addendum,
      stopRecording,
      setSavingState,
      addendumUpdate,
      submitAddendumMutation,
      resolveSubmissionWorkstationID,
      trackTypedCharacters,
      startCountdown,
      isBypassAndSign,
      me,
      isEmptyRequiredFieldsDialogOpen: isEmptyRequiredFieldsDialogOpenForCurrentCase,
      openEmptyRequiredFieldsDialog,
      setAllRequiredFieldsTouched,
    });

  const { handleSubmit, handleCancelSubmit } = useSubmitReport({
    isReporterSubmissionEnabled,
    isPrelimSubmissionEnabled,
    autoloadTemplateState,
    setSavingState,
    fieldsUpdate,
    fieldsUpdateDebounced,
    addendumUpdateDebounced,
    hasHeadingError,
    hasReportContentChanged,
    setHasReportContentChanged,
    worklistItem,
    status,
    setStatus,
    getCurrentReportContent,
    getCurrentReportUpdateType,
    activateTemplateID,
    isRecording,
    stopRecording,
    placeholderFieldDialogState,
    setPlaceholderFieldDialogState,
    isEmptyRequiredFieldsDialogOpenForCurrentCase,
    openEmptyRequiredFieldsDialog,
    setAllRequiredFieldsTouched,
    resolveSubmissionWorkstationID,
    workstationID,
    isBypassAndSign,
    isBypassAndSignPrelim,
    submitReport,
    submitPrelimReport,
    analyticsData,
    enqueueToast,
    enqueueOrUpdateToast,
    latestToastKey,
    setLatestToastKey,
    trackTypedCharacters,
    setPrelimReportWorklistSmid,
    startCountdown,
    measureReportExitActionEndWithMetadata,
    broadcastRefreshCase,
    maybeAutoloadNextCaseCallback,
    isWorklistAutoLoadEnabled,
    setRightDrawerCatalogRefreshSequence,
    cancelCountdown,
    cancelReport,
    cancelPrelimReport,
    submitAddendum,
    cancelAddendumSubmit,
  });

  useEffect(() => {
    if (isFocusModeOn && !isFocusModeAllowed) {
      toggleFocusMode({ method: FocusModeToggleMethod.FormControl, enabled: false });
    }
  }, [isDisabled, isFocusModeOn, isFocusModeAllowed, toggleFocusMode]);

  useEffect(() => {
    if (status === ReportStatus.Submitted && countdownSeconds === null) {
      worklistUpdateBc.postMessage({
        smid: worklistItem.smid,
        reason: WorklistRefetchReason.Countdown,
      });
    }
  }, [status, worklistItem.smid, countdownSeconds]);

  useEffect(() => {
    if (
      status !== ReportStatus.AddendumAdd ||
      previousAddendum?.content == null ||
      addendum?.content === previousAddendum?.content ||
      addendumSMID == null
    ) {
      return;
    }

    // saving state is set to ReporterSaveState.Saving outside of debounced callback, so that state is set as soon as the user makes a change to the addendum
    setSavingState(ReporterSaveState.Saving);

    addendumUpdateDebounced({ contentToSubmit: addendum?.content, addendumSMID });
  }, [addendumUpdateDebounced, addendum, previousAddendum, addendumSMID, status, setSavingState]);

  useReporterInputBindings({
    status,
    isLinkCasesMutating,
    isPrelimSubmissionEnabled,
    isAutoImpressionsEnabled,
    isFocusModeAllowed,
    unsignedReportDialogOpen: unsignedReportDialogOptions.open,
    discardReportOpen: discardReportOptions.open,
    handleSubmit,
    handleSaveDraft,
    handleCancelAddAddendum,
    handleDiscardReport,
    handleOpenDiscardReportModal,
    handleKeepDraft,
    toggleFocusMode,
    onDialogActionSuccess,
    onDialogActionError,
    measureReportExitActionStartWithMetadata,
    fieldsUpdateDebounced,
    apolloQueryContext: APOLLO_QUERY_CONTEXT,
  });

  // Keep the dispatch context stable while its commands continue to use the
  // latest provider state. Content changes are frequent, and dispatch-only
  // consumers should not rerender for them.
  const handleInsertTemplateRef = useLatest(handleInsertTemplate);
  const handleSaveDraftRef = useLatest(handleSaveDraft);
  const handleSubmitRef = useLatest(handleSubmit);
  const handleCancelSubmitRef = useLatest(handleCancelSubmit);
  const handleAddendumAddRef = useLatest(handleAddendumAdd);
  const handleCancelAddAddendumRef = useLatest(handleCancelAddAddendum);

  const contextHandleInsertTemplate = useCallback(
    (insertionContext?: TemplateInsertionContext) =>
      handleInsertTemplateRef.current(insertionContext),
    [handleInsertTemplateRef]
  );
  const contextHandleSaveDraft = useCallback(
    (options?: OnSubmitParams) => handleSaveDraftRef.current(options),
    [handleSaveDraftRef]
  );
  const contextHandleSubmit = useCallback(
    (options?: OnSubmitParams) => handleSubmitRef.current(options),
    [handleSubmitRef]
  );
  const contextHandleCancelSubmit = useCallback(
    () => handleCancelSubmitRef.current(),
    [handleCancelSubmitRef]
  );
  const contextHandleAddendumAdd = useCallback(
    () => handleAddendumAddRef.current(),
    [handleAddendumAddRef]
  );
  const contextHandleCancelAddAddendum = useCallback(
    (options?: OnSubmitParams) => handleCancelAddAddendumRef.current(options),
    [handleCancelAddAddendumRef]
  );

  const dispatchBag = useMemo(
    () => ({
      dispatch,
      getCurrentReportContent,
      handleInsertTemplate: contextHandleInsertTemplate,
      onReportChange,
      handleSubmit: contextHandleSubmit,
      handleAddendumAdd: contextHandleAddendumAdd,
      handleCancelSubmit: contextHandleCancelSubmit,
      handleCancelAddAddendum: contextHandleCancelAddAddendum,
      onAddendumChange,
      handleSaveDraft: contextHandleSaveDraft,
    }),
    [
      dispatch,
      getCurrentReportContent,
      contextHandleInsertTemplate,
      onReportChange,
      contextHandleSubmit,
      contextHandleCancelSubmit,
      contextHandleAddendumAdd,
      contextHandleCancelAddAddendum,
      onAddendumChange,
      contextHandleSaveDraft,
    ]
  );

  const countdownBag = useMemo(() => ({ countdownSeconds }), [countdownSeconds]);
  const hasAddendum = state.addendum != null;
  const readinessBag = useMemo(
    () => ({
      initialStateReconciled: state.initialStateReconciled,
      hasAddendum,
    }),
    [hasAddendum, state.initialStateReconciled]
  );
  return (
    <FieldsStateContext.Provider value={state}>
      <FieldsDispatchContext.Provider value={dispatchBag}>
        <FieldsCountdownContext.Provider value={countdownBag}>
          <FieldsReadinessContext.Provider value={readinessBag}>
            {children}
            <WorkstationIDSubmissionDialog {...workstationPromptDialogProps} />
          </FieldsReadinessContext.Provider>
        </FieldsCountdownContext.Provider>
      </FieldsDispatchContext.Provider>
    </FieldsStateContext.Provider>
  );
};

FieldsProvider.displayName = 'FieldsProvider';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(FieldsProvider);
